import React, { useState, useEffect } from 'react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { publicAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Code2, Copy } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function EmbedGeneratorPage() {
  const [limit, setLimit] = useState(5);
  const [edition, setEdition] = useState('global');
  const [editions, setEditions] = useState([]);
  const [preview, setPreview] = useState([]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const scriptSrc = `${origin}/embed.js`;
  const apiBaseAttr = API_BASE || origin;
  const snippet = `<div id="NeuralNews-widget"></div>
<script async src="${scriptSrc}" data-api-base="${apiBaseAttr}" data-site-url="${origin}" data-limit="${limit}" data-edition="${edition}"></script>`;

  useEffect(() => {
    publicAPI.editions().then((r) => setEditions(r.data.editions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    publicAPI
      .widgetArticles({ limit, edition: edition === 'global' ? undefined : edition })
      .then((r) => setPreview(r.data.articles || []))
      .catch(() => setPreview([]));
  }, [limit, edition]);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => toast.success('Embed code copied')).catch(() => toast.error('Copy failed'));
  };

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen max-w-[900px] mx-auto px-4 sm:px-6 py-10">
        <h1 className="news-masthead text-3xl font-bold mb-2 flex items-center gap-2">
          <Code2 className="w-8 h-8 text-[#B89B5E]" />
          Embed & syndication
        </h1>
        <p className="text-[#5B616E] text-sm mb-8">
          Paste the script on any website to show your latest headlines. The widget calls the public API with open CORS.
        </p>

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="border-[#E7E0D6] bg-white">
            <CardHeader>
              <CardTitle className="text-sm">Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#5B616E] block mb-1">Stories</label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={limit}
                  onChange={(e) => setLimit(Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 5)))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B616E] block mb-1">Edition</label>
                <select
                  className="w-full border border-[#E7E0D6] rounded-md px-3 py-2 text-sm bg-white"
                  value={edition}
                  onChange={(e) => setEdition(e.target.value)}
                >
                  <option value="global">International (all)</option>
                  {editions.map((ed) => (
                    <option key={ed.slug || ed.id} value={ed.slug || ed.id}>
                      {ed.name || ed.slug}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="button" onClick={copy} className="w-full bg-[#121316] text-white hover:bg-[#1B1D22]">
                <Copy className="w-4 h-4 mr-2" />
                Copy embed code
              </Button>
            </CardContent>
          </Card>

          <Card className="border-[#E7E0D6] bg-white">
            <CardHeader>
              <CardTitle className="text-sm">Live preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div id="NeuralNews-widget" className="border border-dashed border-[#E7E0D6] rounded-lg p-4 min-h-[120px]">
                {preview.length === 0 ? (
                  <p className="text-sm text-[#5B616E]">No articles for this filter.</p>
                ) : (
                  <ul className="space-y-3 list-none m-0 p-0">
                    {preview.map((a) => (
                      <li key={a.id} className="flex gap-3 text-sm">
                        {a.featured_image && (
                          <img src={a.featured_image} alt="" className="w-16 h-12 object-cover rounded flex-shrink-0" />
                        )}
                        <div>
                          <span className="font-semibold text-[#121316] leading-snug block">{a.title}</span>
                          {a.category_name && <span className="text-xs text-[#5B616E]">{a.category_name}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-[#E7E0D6] bg-white mt-6">
          <CardHeader>
            <CardTitle className="text-sm">HTML snippet</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-[#121316] text-[#FBF7EF] p-4 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
              {snippet}
            </pre>
          </CardContent>
        </Card>
      </main>
      <PublicFooter />
    </>
  );
}
